import { useQuery } from '@tanstack/react-query'
import TenantsService from '../services/tenants'
import { usePermissions } from './usePermissions'

/**
 * #208 — L'IMPORT INTELLIGENT EST UNE OPTION PAR CLIENT (décision client
 * 2026-10-06 : « je veux préserver la fonction actuelle d'import ; l'import
 * intelligent sera une option du CRM, peut-être payante »).
 *
 * Comment le drapeau voyage jusqu'à l'écran : `Tenant.smartImportEnabled`
 * (base) → `GET /tenants/me/options` (tenant du JWT, lu en base à chaque
 * appel — pas dans le JWT, qui peut être périmé ; même route que la pause du
 * plateau, dont on ne lit ici que `smartImportEnabled`) → cette requête
 * TanStack → la carte « Import intelligent » en tête de l'ancien écran
 * d'import, et la porte de `/import/intelligent` (`SmartImportGate`).
 *
 * Qui demande : un ADMIN seulement (le super-admin est un ADMIN du tenant
 * Système : même règle que l'API d'import, `SmartImportGuard`). La route
 * d'options, elle, se lit par tout utilisateur ; pour les autres rôles la
 * requête ne part pas — l'import leur serait refusé de toute façon — et
 * l'accès vaut `not-admin`.
 *
 * `disabled` couvre aussi une réponse en erreur : l'écran ne propose jamais
 * ce que l'API refuserait, et l'API reste seule juge (`SmartImportGuard`).
 */
export const TENANT_OPTIONS_KEY = ['tenants', 'me', 'options'] as const

export type SmartImportAccess = 'loading' | 'enabled' | 'disabled' | 'not-admin'

export function useSmartImportOption(): { access: SmartImportAccess; enabled: boolean } {
  const { isAdmin, isLoading: userLoading } = usePermissions()
  const query = useQuery({
    queryKey: TENANT_OPTIONS_KEY,
    queryFn: () => TenantsService.getMyOptions(),
    enabled: isAdmin,
    // Une option se coupe et se rallume rarement : une minute de cache suffit,
    // et une page qui revient au premier plan la relit.
    staleTime: 60_000,
    retry: false,
  })

  let access: SmartImportAccess
  if (userLoading) access = 'loading'
  else if (!isAdmin) access = 'not-admin'
  else if (query.data) access = query.data.smartImportEnabled ? 'enabled' : 'disabled'
  else if (query.isError) access = 'disabled'
  else access = 'loading'

  return { access, enabled: access === 'enabled' }
}
